import { get } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GetRecords from 'src/lib/api/GetRecords';
import { PostRecord } from 'src/lib/api/PostRecord';
import { APIS } from 'src/lib/constants/apis';
import { ANTRAGS_FORMULAREN, KOSTEN_ARTEN, KOSTEN_POSITION } from 'src/lib/constants/constant';
import { PAGES_URLS } from 'src/lib/constants/routes';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import { removeDataKeys, removeNullDeep } from 'src/utils/helper';
import { useAuthentication } from '../../../../../context/AuthContext';
import { handleCreateKostenPositions } from './kostenPositionsHelper';

// import Schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenKategorieSchema from 'src/lib/paths/kostenkategorie_FULL_Paths.json';
const P = AntragSchema.attributes;
const KK_P = KostenKategorieSchema.attributes;

interface UseApplicationCreationResult {
	wsActive: boolean;
	wsError: boolean;
	createApplication: (antragDetailsObject: any, appData: any) => Promise<void>;
	setWsError: (error: boolean) => void;
}

export const useApplicationCreation = (): UseApplicationCreationResult => {
	const navigate = useNavigate();
	const [wsActive, setWsActive] = useState(false);
	const [wsError, setWsError] = useState(false);
	const { authenticatedData } = useAuthentication();

	const createApplication = async (antragDetailsObject: any, appData: any) => {
		const antragsFormular = get(antragDetailsObject, 'Antragsformular');

		try {
			setWsActive(true);
			const kostenKategoreis = await fetchKostenKategories(antragsFormular);
			const res = await PostRecord(APIS.Antrag, { data: appData });
			const result = removeNullDeep(removeDataKeys(get(res, 'data', [])));

			if (result) {
				/**
				 * Nur Bei Messe ist sonderfall.
				 * Weil da die Kostenkategorien immer fix definiert sind und muss beim Erstellen des Antrags direkt die Kostenpositionen mit erstellt werden.
				 */
				if (antragsFormular === ANTRAGS_FORMULAREN.Messefoerderung) {
					const allKostenKategoreis = kostenKategoreis || [];
					for (const eachKostenKategoreis of allKostenKategoreis) {
						const kostenPositionData = {
							Foerderantrag: get(res, 'data.id'),
							Allgemein_Kostentraeger: 'Antragsteller',
							Unternehmen: get(authenticatedData, 'organisationId'),
							Allgemein_Kostenposition: get(eachKostenKategoreis, KK_P.Allgemein_Kostenposition.path),
							Allgemein_Kostenart_Finanzierung: get(eachKostenKategoreis, KK_P.Allgemein_Kostenart_Finanzierung.path),
							Allgemein_Kostenkategorie: get(eachKostenKategoreis, 'id'),
						};
						await PostRecord(APIS.Kostenposition, { data: kostenPositionData });
					}
				} else if (antragsFormular === ANTRAGS_FORMULAREN.EmobLis) {
					// Create Investitions kosten

					get(appData, 'Projektbeschreibung_Elektromobilitaet_LIS.Standort_Spezifikation_Ladeinfrastruktur', []).forEach(async (ele: any) => {
						const groupId = get(ele, 'Group_45_ID', '');
						if (groupId) {
							const kostenPositionData = {
								Foerderantrag: get(res, 'data.id'),
								Allgemein_Kostentraeger: 'Antragsteller',
								Unternehmen: get(authenticatedData, 'organisationId'),
								Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
								Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Investitionskosten,
								Allgemein_Standort_Group_ID: get(ele, 'Group_45_ID'),
							};
							await PostRecord(APIS.Kostenposition, { data: kostenPositionData });
						}
					});
					//  Create first Eigenmittel
					const kostenPositionData = {
						Foerderantrag: get(res, 'data.id'),
						Allgemein_Kostentraeger: 'Antragsteller',
						Unternehmen: get(authenticatedData, 'organisationId'),
						Allgemein_Kostenposition: KOSTEN_POSITION.Einnahme,
						Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Eigenmittel,
					};
					await PostRecord(APIS.Kostenposition, { data: kostenPositionData });
				} else {
					await handleCreateKostenPositions(get(authenticatedData, 'organisationId', -1), get(result, 'id'), kostenKategoreis);
				}

				navigate(PAGES_URLS.OnlineAntrag, {
					state: {
						id: res.data.id,
						api: APIS.Antrag,
						antragsformular: get(result, P.Allgemein_Antragsformular.path, ''),
					},
				});
			}
		} catch (err) {
			setWsError(true);
			console.error('ERROR: Creating Application: ', err);
		} finally {
			setWsActive(false);
		}
	};

	const fetchKostenKategories = async (antragsFormular: string) => {
		const filter = withKostenkategorieEinsatzbereichFilter(
			{
				Allgemein_Antragsformular: { $contains: antragsFormular },
				Allgemein_Aktiv: { $eq: true },
			},
			APIS.Antrag,
		);
		const res = await GetRecords(APIS.Kostenkategorie, [], filter);
		return removeNullDeep(removeDataKeys(get(res, 'data', [])));
	};

	return { wsActive, wsError, createApplication, setWsError };
};
